import { updateUrlParam } from '@/ui/utils/client-url-helpers';
import { urlParameterCodes } from '@/constants/urlParams';
import { BaseProduct } from '@/ui/services/BaseProductService';
import { ParsedProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedProductVariant';
import { getPriceOrInitialPeriodPrice } from '@/ui/utils/currency-helpers';
import { generateRandomId } from '@/ui/utils/helpers';
import { DocShapeId } from '@/utils/shapeIds';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import { TrackingConversionType } from '@/utils/tracking-conversion-types';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { getExperimentTrackingProps } from '@/utils/experiment-helper';
import { DeliveryTimeRange } from '@/integrations/crystallize/types/localization/DeliveryTimeRange';
import { getCartItemMergeKey } from '@/utils/cart-line-grouping';
import {
  DEFAULT_MAX_DELIVERY_TIME_FALLBACK,
  DEFAULT_MIN_DELIVERY_TIME_FALLBACK,
} from '@/utils/constants';
import { CartBundleItem, CartItem, ICartState } from '../store/cart/types';
import { CostBreakdown } from '../components/pages/checkout/CheckoutForm/types/CostBreakdown';
import { calculateCostBreakdown } from '../utils/costBreakdownCalculation';

const sortedMergeKeys = (items: CartItem[]): string[] =>
  items.map((item) => getCartItemMergeKey(item)).sort((a, b) => a.localeCompare(b));

export class CartService {
  /**
   * Calculates the delivery time based on the product in cart with longest delivery time.
   * @param {ICartState} state - The current state of the cart.
   * @returns {DeliveryTime} - The delivery time of the product with the longest delivery time.
   */
  public static getDeliveryTime(state: ICartState): DeliveryTimeRange {
    const { products } = state.items;
    const { bundles } = state.items;
    const allItems = [
      ...products,
      ...bundles.reduce((acc, bundle) => [...acc, ...bundle.productVariants], [] as CartItem[]),
    ];
    const deliveryTimes = allItems.map((item) => item.deliveryTime);
    const { areaVariables } = state;
    const maxDeliveryTime = {
      min: Math.max(
        ...deliveryTimes.map((dt) =>
          dt?.min != null ? dt.min : DEFAULT_MIN_DELIVERY_TIME_FALLBACK,
        ),
      ),
      max: Math.max(
        ...deliveryTimes.map((dt) =>
          dt?.max != null ? dt.max : DEFAULT_MAX_DELIVERY_TIME_FALLBACK,
        ),
      ),
    };

    if (areaVariables?.deliveryZone) {
      maxDeliveryTime.min += areaVariables.deliveryZone.correctionMinDeliveryTime ?? 0;
      maxDeliveryTime.max += areaVariables.deliveryZone.correctionMaxDeliveryTime ?? 0;
    }
    return maxDeliveryTime;
  }

  /**
   * Calculates the total cost breakdown of the cart, including tax and shipping.
   * @param {ICartState} state - The current state of the cart.
   * @param {number} shippingCost - The shipping cost to be applied.
   * @returns {CostBreakdown} - The cost breakdown of the cart.
   */
  public static getCostBreakdown(state: ICartState, shippingCost: number = 0): CostBreakdown {
    const totalBasePrice = this.calculateTotalCartValue(state);
    const totalDiscount = this.calculateTotalCartDiscount(state);
    const taxPercentage = state.areaVariables?.tax ?? 0;

    return calculateCostBreakdown(shippingCost, totalBasePrice, totalDiscount, taxPercentage);
  }

  /**
   * Counts the number of products in the cart based on the specified attribute.
   * @param {ICartState} state - The current state of the cart.
   * @param {string} attributeKey - The key of the attribute to match (e.g., 'id', 'shape' or 'sku').
   * @param {string} attributeValue - The value of the attribute to match.
   * @returns {number} - The count of products that match both the product ID and the attribute criteria.
   */
  public static countProductsInCartByAttribute(
    state: ICartState,
    attributeKey: 'id' | 'shape' | 'sku',
    attributeValue: string,
  ): number {
    let count = 0;

    // Count products that match the product ID and the attribute in the main products array
    count += state.items.products.filter((p) => p[attributeKey] === attributeValue).length;

    // Count products that match the product ID and the attribute in all bundles
    state.items.bundles.forEach((bundle) => {
      count += bundle.productVariants.filter((pv) => pv[attributeKey] === attributeValue).length;
    });

    return count;
  }

  /**
   * Public method to check if the cart contains only virtual products.
   * @param {ICartState} state - The current state of the cart.
   * @returns {boolean} - True if the cart contains only virtual products, otherwise false.
   */
  public static containsOnlyVirtualProducts(state: ICartState): boolean {
    if (state.items.bundles.length > 0) {
      return false; // Bundles always contain physical products
    }
    return state.items.products.every((item) => this.isVirtualProduct(item));
  }

  /**
   * Public method to check if the cart contains specific product by crystallize product ID (any variant).
   * @param {ICartState} state - The current state of the cart.
   * @param {string} productId - The current state of the cart.
   * @returns {boolean} - True if the cart contains only virtual products, otherwise false.
   */
  public static containsProductById(state: ICartState, productId: string): boolean {
    const productFoundInProducts = state.items.products.some((product) => product.id === productId);
    const productFoundInBundles = state.items.bundles.some((bundle) => {
      const bundleContainsProduct = bundle.productVariants.some(
        (bundleProduct) => bundleProduct.id === productId,
      );
      return bundleContainsProduct;
    });
    return productFoundInProducts || productFoundInBundles;
  }

  /**
   * Checks if any item in the cart (either individual product or within a bundle) has a matching nukiId.
   * @param {ICartState} state - The current state of the cart.
   * @param {string} nukiId - The nukiId to search for.
   * @param {string} sku - The sku of the product to be checked.
   * @returns {boolean} - True if any item in the cart has the given nukiId, otherwise false.
   */
  public static isThereAnItemLinkedWithID(state: ICartState, nukiId: string, sku: string): boolean {
    // Check in individual products
    const hasMatchingProduct = state.items.products.some(
      (product) => product.nukiId === nukiId && product.sku === sku,
    );
    if (hasMatchingProduct) {
      return true;
    }

    // Check in bundles
    const hasMatchingBundleItem = state.items.bundles.some((bundle) =>
      bundle.productVariants.some((variant) => variant.nukiId === nukiId),
    );

    return hasMatchingBundleItem;
  }

  /**
   * Adds a bundle directly to the cart.
   * @param {ICartState} state - The current state of the cart.
   * @param {CartBundleItem} bundle - The bundle to add to the cart.
   * @returns {ICartState} - The updated state with the new bundle added.
   */
  static addBundleToCart(state: ICartState, bundle: CartBundleItem): ICartState {
    const updatedBundles = this.updateCartBundles(state, bundle);

    const newState: ICartState = {
      ...state,
      items: { products: state.items.products, bundles: updatedBundles },
      lastAddedToCart: bundle,
      lastUpdated: new Date().toISOString(),
    };

    this.sendTrackingForBundleCartUpdate(newState, bundle, TrackingConversionType.ADD_TO_CART);

    return newState;
  }

  /**
   * Updates the bundles in the cart by adding a new bundle
   * @param state
   * @param {CartBundleItem} newBundle - The new bundle to add or update.
   * @returns {CartBundleItem[]} - The updated array of cart bundles.
   */
  private static updateCartBundles(state: ICartState, newBundle: CartBundleItem): CartBundleItem[] {
    const { bundles } = state.items;

    const existingBundleIndex = bundles.findIndex((bundle) =>
      this.areBundlesIdentical(bundle, newBundle),
    );

    if (existingBundleIndex !== -1) {
      const updatedBundles = bundles.map((bundle, index) => {
        if (index === existingBundleIndex) {
          return { ...bundle, quantity: bundle.quantity + newBundle.quantity };
        }
        return bundle;
      });

      return updatedBundles;
    }
    return [...bundles, newBundle];
  }

  /**
   * Method to add a cart item to cart (or update quantity of existing item).
   * @param {ICartState} state - The current state of the cart.
   * @param {CartItem} newItem - The new item to add or update in the cart.
   * @returns {ICartState} - The updated state with the new or updated item.
   */
  static addItemToCart(state: ICartState, newItem: CartItem): ICartState {
    // Check if product with given nuki ID is already in the cart and only add if it's not
    if (newItem.nukiId && this.isThereAnItemLinkedWithID(state, newItem.nukiId, newItem.sku)) {
      return state;
    }

    const updatedProducts = this.updateCartItems(state.items.products, newItem);

    const newState = {
      ...state,
      items: { products: updatedProducts, bundles: state.items.bundles },
      lastAddedToCart: newItem,
      lastUpdated: new Date().toISOString(),
    };

    this.sendTrackingForProductCartUpdate(newState, newItem, TrackingConversionType.ADD_TO_CART);

    return newState;
  }

  /**
   * Adds a product to the cart, adjusting for discounts and quantity updates.
   * @param {ICartState} state - The current state of the cart.
   * @param {BaseProduct} product - The product to add.
   * @param {ParsedProductVariant} productVariant - The variant of the product to add.
   * @param {Object} options - Optional parameters including discount and whether quantity should be fixed.
   * @returns {ICartState} - The updated state with the new product added.
   */
  static addProductToCart(
    state: ICartState,
    product: BaseProduct,
    productVariant: ParsedProductVariant,
    options?: {
      discount?: number;
      fixedQuantity?: boolean;
      activationCode?: string;
      nukiId?: string;
      quantity?: number;
    },
  ): ICartState {
    const newProductItem: CartItem = {
      id: product.id,
      cartItemId: generateRandomId(),
      url: product.path,
      shape: product.shape,
      name: productVariant.name,
      sku: productVariant.sku,
      priceVariant: productVariant.priceVariant,
      quantity: options?.quantity ?? 1,
      image: productVariant.images[0],
      discount: options?.discount,
      fixedQuantity: options?.fixedQuantity,
      nukiId: options?.nukiId,
      activationCode: options?.activationCode,
      subscription: productVariant.subscriptionPlan?.periods[0]
        ? {
            period: productVariant.subscriptionPlan?.periods[0],
            subscriptionPlanId: productVariant.subscriptionPlan.identifier,
          }
        : undefined,
      deliveryTime: product.getDeliveryTimeRange(productVariant),
    };

    return this.addItemToCart(state, newProductItem);
  }

  /**
   * Removes a product from the cart by its cartItemId.
   * @param {ICartState} state - The current state of the cart.
   * @param {string} cartItemId - The cartItemId of the product to be removed.
   * @returns {ICartState} - The updated state of the cart with the product removed.
   */
  public static removeProductFromCart(state: ICartState, cartItemId: string): ICartState {
    const updatedProducts = state.items.products.filter(
      (product) => product.cartItemId !== cartItemId,
    );

    const productToDelete = state.items.products.filter(
      (product) => product.cartItemId === cartItemId,
    );

    const newState = {
      ...state,
      items: { ...state.items, products: updatedProducts },
      lastUpdated: new Date().toISOString(),
    };

    if (productToDelete.length > 0) {
      this.sendTrackingForProductCartUpdate(
        newState,
        productToDelete[0],
        TrackingConversionType.DELETE_FROM_CART,
      );
    }

    return newState;
  }

  /**
   * Removes an entire bundle from the cart using the cart bundle ID.
   * @param {ICartState} state - The current state of the cart.
   * @param {string} cartBundleId - The ID of the bundle to be removed.
   * @returns {ICartState} - The updated state of the cart with the bundle removed.
   */
  public static removeBundleFromCart(
    state: ICartState,
    cartBundleId: string,
    upgradeProgramDeviceId?: string,
  ): ICartState {
    const updatedBundles = state.items.bundles.filter(
      (bundle) =>
        bundle.cartBundleId !== cartBundleId ||
        bundle.upgradeProgramDeviceId !== upgradeProgramDeviceId,
    );
    const bundleToDelete = state.items.bundles.filter(
      (bundle) =>
        bundle.cartBundleId === cartBundleId &&
        bundle.upgradeProgramDeviceId === upgradeProgramDeviceId,
    );

    const newState = {
      ...state,
      items: { ...state.items, bundles: updatedBundles },
      lastUpdated: new Date().toISOString(),
    };

    if (bundleToDelete.length > 0) {
      this.sendTrackingForBundleCartUpdate(
        newState,
        bundleToDelete[0],
        TrackingConversionType.DELETE_FROM_CART,
      );
    }
    return newState;
  }

  /**
   * Updates the quantity of an item in the cart.
   * @param {ICartState} state - The current state of the cart.
   * @param {string} cartItemId - cartItemId of the item whose quantity needs updating.
   * @param {number} quantity - The new quantity to set.
   * @returns {ICartState} - The updated state with the modified quantity.
   */
  public static updateItemQuantityInCart(
    state: ICartState,
    cartItemId: string,
    quantity: number,
  ): ICartState {
    if (quantity < 1) {
      return { ...state };
    }
    let isAddItem = false;
    let updatedProduct: CartItem | undefined;
    const updatedProducts = state.items.products.map((item) => {
      if (item.cartItemId === cartItemId) {
        if (item.quantity < quantity) {
          isAddItem = true;
        }
        updatedProduct = item;
        return { ...item, quantity };
      }
      return item;
    });

    const newState = {
      ...state,
      items: { products: updatedProducts, bundles: state.items.bundles },
      lastUpdated: new Date().toISOString(),
    };

    if (updatedProduct) {
      if (isAddItem) {
        this.sendTrackingForProductCartUpdate(
          newState,
          updatedProduct,
          TrackingConversionType.ADD_TO_CART,
        );
      } else {
        this.sendTrackingForProductCartUpdate(
          newState,
          updatedProduct,
          TrackingConversionType.REMOVE_FROM_CART,
        );
      }
    }

    return newState;
  }

  /**
   * Updates the quantity of a bundle in the cart.
   * @param {ICartState} state - The current state of the cart.
   * @param {string} cartBundleId - The ID of the bundle whose quantity needs updating.
   * @param {number} quantity - The new quantity to set for the bundle.
   * @returns {ICartState} - The updated state with the modified bundle quantity.
   */
  public static updateBundleQuantityInCart(
    state: ICartState,
    cartBundleId: string,
    quantity: number,
  ): ICartState {
    if (quantity < 1) {
      // Optionally, remove the bundle if the quantity is set to less than 1
      return { ...state };
    }

    let isAddItem = false;
    let updatedBundle: CartBundleItem | undefined;

    const updatedBundles = state.items.bundles.map((bundle) => {
      if (bundle.cartBundleId === cartBundleId) {
        if (bundle.quantity < quantity) {
          isAddItem = true;
        }
        updatedBundle = bundle;
        return { ...bundle, quantity };
      }
      return bundle;
    });

    const newState = {
      ...state,
      items: { products: state.items.products, bundles: updatedBundles },
      lastUpdated: new Date().toISOString(),
    };

    if (updatedBundle) {
      if (isAddItem) {
        this.sendTrackingForBundleCartUpdate(
          newState,
          updatedBundle,
          TrackingConversionType.ADD_TO_CART,
        );
      } else {
        this.sendTrackingForBundleCartUpdate(
          newState,
          updatedBundle,
          TrackingConversionType.REMOVE_FROM_CART,
        );
      }
    }
    return newState;
  }

  /**
   * Calculates the total price of a bundle.
   * @param {CartBundleItem} bundle - A bundle containing multiple product variants.
   * @returns {number} - The total price of all product variants in the bundle.
   */
  static calculateBundlePrice(bundle: CartBundleItem): number {
    return bundle.productVariants.reduce(
      (total, item) =>
        total +
        (getPriceOrInitialPeriodPrice(item.priceVariant, item.subscription?.period) || 0) *
          item.quantity,
      0,
    );
  }

  /**
   * Calculates the total discount for a bundle.
   * @param {CartBundleItem} bundle - A bundle containing multiple product variants.
   * @returns {number} - The total discount for all product variants in the bundle.
   */
  static calculateBundleDiscount(bundle: CartBundleItem): number {
    // No clamp here: cart items are clamped to the charged price when they are created, order
    // pages pass already-discounted prices and rebuild the original as price plus discount, and
    // the display floors a negative item price at zero.
    return bundle.productVariants.reduce(
      (total, item) => total + (item.discount || 0) * item.quantity,
      0,
    );
  }

  /**
   * Calculates the total price of all items in the cart.
   * @param {ICartState} state - The current state of the cart.
   * @returns {number} - The total price of all items in the cart.
   */
  static calculateTotalCartValue(state: ICartState): number {
    const productsTotal = state.items.products.reduce(
      (total, item) =>
        total +
        (getPriceOrInitialPeriodPrice(item.priceVariant, item.subscription?.period) || 0) *
          item.quantity,
      0,
    );
    const bundlesTotal = state.items.bundles.reduce(
      (total, bundle) => total + this.calculateBundlePrice(bundle) * bundle.quantity,
      0,
    );
    return productsTotal + bundlesTotal;
  }

  /**
   * Calculates the total discount for all items in the cart.
   * @param {ICartState} state - The current state of the cart.
   * @returns {number} - The total discount for all items.
   */
  static calculateTotalCartDiscount(state: ICartState): number {
    const productsTotal = state.items.products.reduce(
      (total, item) => total + (item.discount || 0) * item.quantity,
      0,
    );
    const bundlesTotal = state.items.bundles.reduce(
      (total, bundle) => total + this.calculateBundleDiscount(bundle) * bundle.quantity,
      0,
    );
    return productsTotal + bundlesTotal;
  }

  /**
   * Calculates the total cart value after discount.
   * @param {ICartState} state - The current state of the cart.
   * @returns {number} - The total discount for all items.
   */
  static calculateTotalCartValueAfterDiscount(state: ICartState): number {
    return this.calculateTotalCartValue(state) - this.calculateTotalCartDiscount(state);
  }

  /**
   * Calculates the total number of items in the cart.
   * @param {ICartState} state - The current state of the cart.
   * @returns {number} - The total number of items in the cart.
   */
  static calculateTotalNumberOfItems(state: ICartState): number {
    const productsCount = state.items.products.reduce(
      (count, product) => count + product.quantity,
      0,
    );
    const bundlesCount = state.items.bundles.reduce(
      (count, bundle) =>
        count + bundle.productVariants.reduce((subCount, item) => subCount + item.quantity, 0),
      0,
    );
    return productsCount + bundlesCount;
  }

  /**
   * Checks if the cart is empty.
   * @param {ICartState} state - The current state of the cart.
   * @returns {boolean} - True if the cart is empty, otherwise false.
   */
  static isCartEmpty(state: ICartState): boolean {
    return this.calculateTotalNumberOfItems(state) === 0;
  }

  /**
   * Toggles the display state of the cart and updates the URL parameter accordingly.
   * @param {ICartState} state - The current state of the cart.
   * @returns {ICartState} - The updated state with the new display status.
   */
  static toggleCartDisplayState(state: ICartState): ICartState {
    const newDisplayState = !state.isCartDisplayed;
    updateUrlParam(urlParameterCodes.cart, newDisplayState ? 'true' : null);
    // The transient display flags (showCartUpselling, showAdditionalInformation, lastAddedToCart)
    // are NOT reset here on close — resetting them synchronously flips/empties the drawer content
    // mid-leave-animation (cart flash + "added to cart" banner vanishing). They are reset via the
    // drawer's afterLeave (resetCartDisplayTransient) once the close animation completes.
    return {
      ...state,
      isCartDisplayed: newDisplayState,
    };
  }

  /**
   * Private method to update cart items, either by adding a new item or increasing the quantity of an existing item.
   * @param {CartItem[]} items - The current array of cart items.
   * @param {CartItem} newItem - The new item to add or whose quantity needs updating.
   * @returns {CartItem[]} - The updated array of cart items.
   */
  private static updateCartItems(items: CartItem[], newItem: CartItem): CartItem[] {
    const updatedItems = [...items];
    const index = items.findIndex((item) => this.haveSameSKUAndPrice(item, newItem));
    const isExistingItem = index !== -1;
    const canUpdateQuantity =
      isExistingItem && !updatedItems[index].fixedQuantity && !newItem.fixedQuantity;

    if (canUpdateQuantity) {
      updatedItems[index].quantity += newItem.quantity;
    } else {
      updatedItems.push({ ...newItem });
    }
    return updatedItems;
  }

  /**
   * Private method to check if two cart items have the same SKU and price.
   * @param {CartItem} product1 - The first product to compare.
   * @param {CartItem} product2 - The second product to compare.
   * @returns {boolean} - True if both products have the same SKU and price, otherwise false.
   */
  private static haveSameSKUAndPrice(product1: CartItem, product2: CartItem): boolean {
    return (
      product1.sku === product2.sku &&
      product1.priceVariant?.price === product2.priceVariant?.price &&
      product1.discount === product2.discount
    );
  }

  /**
   * Private method to check if two bundles are the same and with the same product variants selected.
   * @param {CartBundleItem} bundle1 - The first bundle to compare.
   * @param {CartBundleItem} bundle2 - The second bundle to compare.
   * @returns {boolean} - True if both bundles are identical, otherwise false.
   */
  private static areBundlesIdentical(bundle1: CartBundleItem, bundle2: CartBundleItem): boolean {
    if (bundle1.id !== bundle2.id) {
      return false;
    }
    if (bundle1.productVariants.length !== bundle2.productVariants.length) {
      return false;
    }
    if (bundle1.upgradeProgramDeviceId || bundle2.upgradeProgramDeviceId) {
      if (bundle1.upgradeProgramDeviceId !== bundle2.upgradeProgramDeviceId) {
        return false;
      }
    }
    // Slot order depends on the order the customer picked colours in, not on what the set holds.
    const keys1 = sortedMergeKeys(bundle1.productVariants);
    const keys2 = sortedMergeKeys(bundle2.productVariants);
    return keys1.every((key, index) => key === keys2[index]);
  }

  /**
   * Private method to check if a cart item is a virtual product.
   * @param {CartItem} item - The cart item to check.
   * @returns {boolean} - True if the item is a virtual product, otherwise false.
   */
  private static isVirtualProduct(item: CartItem): boolean {
    const shapeId = item.shape;
    return shapeId === DocShapeId.subscription || shapeId === DocShapeId.digitalProduct;
  }

  private static async sendTrackingForBundleCartUpdate(
    state: ICartState,
    bundle: CartBundleItem,
    action: TrackingConversionType,
  ) {
    axios.post(apiPaths.metadata.trackConversions, {
      conversion: action,
    });

    // GTM
    DataLayerHelper.pushBundleCartUpdate(
      bundle,
      action === TrackingConversionType.ADD_TO_CART ? 'add' : 'remove',
      state.getPriceVariant()?.currency,
    );

    // Bloomreach
    if (window.exponea) {
      const { total, totalWithoutTax } = this.getCostBreakdown(state, 0);
      window.exponea.track('cart_update', {
        action,
        product_list: state.items,
        total_quantity: state.numberOfItems(),
        total_price: total,
        product_id: bundle.documentExtRef,
        variant_id: bundle.productVariants[0].id,
        title: bundle.name,
        price: bundle.productVariants.reduce(
          (a, b) => a + ((b.priceVariant?.price || 0) - (b.discount || 0)),
          0,
        ),
        currency: state.getPriceVariant()?.currency || 'unknown',
        domain: 'nuki.io',
        language: state.country,
        product_ids: [
          ...state.items.products.map((i) => i.id),
          ...state.items.bundles.map((b) => b.documentExtRef),
        ],
        variant_list: bundle.productVariants,
        variant_ids: bundle.productVariants.map((v) => v.id),
        total_price_without_tax: totalWithoutTax,
        ...getExperimentTrackingProps(),
      });
    }
  }

  private static sendTrackingForProductCartUpdate(
    state: ICartState,
    product: CartItem,
    action: TrackingConversionType,
  ) {
    axios.post(apiPaths.metadata.trackConversions, {
      conversion: action,
    });

    const { total, totalWithoutTax } = this.getCostBreakdown(state, 0);

    DataLayerHelper.pushProductCartUpdate(
      product,
      action === TrackingConversionType.ADD_TO_CART ? 'add' : 'remove',
      state.getPriceVariant()?.currency,
    );

    if (window.exponea) {
      window.exponea.track('cart_update', {
        action,
        product_list: state.items,
        total_quantity: state.numberOfItems(),
        total_price: total,
        product_id: product.id,
        variant_id: product.sku,
        title: product.name,
        price: product.priceVariant?.price,
        currency: state.getPriceVariant()?.currency || 'unknown',
        domain: 'nuki.io',
        language: state.country,
        product_ids: [
          ...state.items.products.map((i) => i.id),
          ...state.items.bundles.map((b) => b.documentExtRef),
        ],
        total_price_without_tax: totalWithoutTax,
        ...getExperimentTrackingProps(),
      });
    }
  }
}
